import { cn } from "@tellescope/design-system";
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
  Combobox,
  type ComboboxOption,
  type ComboboxOptionRenderProps,
} from "@tellescope/ui";
import { formatInitials, formatName } from "@tellescope/utils";
import { Check, X } from "lucide-react";
import { useMemo } from "react";
import { BlockProps } from "../../../types";
import { useCareTeamBlock, type CareTeamUser } from "../../../hooks/use-care-team-block";
import { BlockHeader } from "../BlockHeader";
import { BlockContent } from "../BlockContent";
import { BlockEmptyState } from "../BlockEmptyState";
import { useTranslation } from "react-i18next";

export const CareTeamBlock = ({
  enduserId,
  info,
  className,
  viewId,
  blockId,
  blockIndex,
  onMaximize,
  editMode: providedEditMode,
  setEditMode: providedSetEditMode,
  alwaysShowButtons = false,
  contentClassName,
}: BlockProps) => {
  const { t } = useTranslation("contacts");
  const {
    parsedInfo,
    members,
    addOptions,
    addMember,
    removeMember,
    isEditingTitle,
    setIsEditingTitle,
    newTitle,
    setNewTitle,
    editMode,
    setEditMode,
    updateInfo,
    duplicateBlock,
    deleteBlock,
    toggleExpanded,
    expanded,
    blockLayout,
    canEditTitle,
    canDeleteBlock,
    canCreateBlock,
    canEditEnduser,
  } = useCareTeamBlock({
    enduserId,
    info,
    editMode: providedEditMode,
    setEditMode: providedSetEditMode,
    viewId,
    blockId,
    blockIndex,
  });

  const addComboboxOptions = useMemo<ComboboxOption<CareTeamUser>[]>(
    () =>
      addOptions.map((user) => ({
        value: user.id,
        label: formatName(user),
        metadata: { user },
      })),
    [addOptions]
  );

  const formatOption = useMemo(
    () => (props: ComboboxOptionRenderProps<CareTeamUser>) => {
      const user = props.option.metadata?.user;
      if (!user) return null;
      return (
        <div className="flex gap-2 items-center w-full">
          <Avatar className="h-8 w-8">
            <AvatarImage src={user.avatar} />
            <AvatarFallback useStaticBackground={true} id={user.id}>
              {formatInitials(user)}
            </AvatarFallback>
          </Avatar>
          <p className="font-semibold text-sm truncate flex-1 min-w-0">{formatName(user)}</p>
          <Check className={cn("ml-2 h-4 w-4 shrink-0", props.isSelected ? "opacity-100" : "opacity-0")} />
        </div>
      );
    },
    []
  );

  return (
    <div className={cn("group", className)}>
      <BlockHeader
        editMode={editMode}
        setEditMode={setEditMode}
        isEditingTitle={isEditingTitle}
        setIsEditingTitle={setIsEditingTitle}
        newTitle={newTitle}
        setNewTitle={setNewTitle}
        canEditTitle={canEditTitle}
        parsedInfo={parsedInfo}
        updateInfo={updateInfo}
        duplicateBlock={duplicateBlock}
        deleteBlock={deleteBlock}
        canCreateBlock={canCreateBlock}
        canDeleteBlock={canDeleteBlock}
        onMaximize={onMaximize}
        alwaysShowButtons={alwaysShowButtons}
      />
      {editMode && canEditEnduser && (
        <Combobox
          multiple
          value={[]}
          onChange={(options) => {
            const added = options.at(-1)?.value;
            if (added) addMember(added);
          }}
          options={addComboboxOptions}
          placeholder={t("Search and select to add...")}
          searchPlaceholder={t("Search by name, email, or phone...")}
          loadingText={t("Searching...")}
          emptyText={t("No users found")}
          clearable={false}
          formatOption={formatOption}
          className="w-full pt-2"
        />
      )}
      <BlockContent
        editMode={editMode}
        setEditMode={setEditMode}
        expanded={expanded}
        layout={blockLayout}
        onToggleExpanded={() => toggleExpanded()}
        className={cn("flex flex-col gap-1", contentClassName)}
      >
        {members.length > 0 ? (
          members.map((user) => (
            <div key={user.id} className="flex items-center gap-2 py-1">
              <Avatar className="h-8 w-8">
                <AvatarImage src={user.avatar} />
                <AvatarFallback useStaticBackground={true} id={user.id}>
                  {formatInitials(user)}
                </AvatarFallback>
              </Avatar>
              <p className="text-sm truncate flex-1 min-w-0 text-left font-semibold">{formatName(user)}</p>
              {editMode && canEditEnduser && (
                <button
                  className="text-muted-foreground hover:text-foreground shrink-0"
                  onClick={() => removeMember(user.id)}
                >
                  <X size={16} />
                </button>
              )}
            </div>
          ))
        ) : (
          <BlockEmptyState>{t("No care team members assigned")}</BlockEmptyState>
        )}
      </BlockContent>
    </div>
  );
};
